import * as THREE from 'three';
import { distance, angleBetweenNormals, circleFrom3Points, type V3 } from './measureMath';

export type MeasureMode = 'distance' | 'angle' | 'radius';

/** A snapped pick on the model, in Three.js world space (Y-up). */
export interface MeasurePick {
  point: THREE.Vector3;
  normal: THREE.Vector3;
  kind: 'vertex' | 'edge' | 'face' | 'surface';
  /** The flat face under the cursor: outline and its snap targets (hints). */
  face?: { corners: THREE.Vector3[]; center: THREE.Vector3 | null; edges: Array<[THREE.Vector3, THREE.Vector3]> };
}

interface Measurement {
  objects: THREE.Object3D[];
  label: HTMLDivElement;
  anchor: THREE.Vector3;
}

const PICKS_NEEDED: Record<MeasureMode, number> = { distance: 2, angle: 2, radius: 3 };
const PROMPTS: Record<MeasureMode, string[]> = {
  distance: ['Distance: click first point', 'Distance: click second point'],
  angle: ['Angle: click first face', 'Angle: click second face'],
  radius: ['Radius: click 1st point on the arc', 'Radius: click 2nd point', 'Radius: click 3rd point'],
};
const SNAP_COLORS: Record<MeasurePick['kind'], number> = {
  vertex: 0x00d084, edge: 0x4aa3ff, face: 0xffb020, surface: 0xbbbbbb,
};
const LINE_COLOR = 0xff7a1a;

/** Three.js world (Y-up) → SCAD (Z-up). */
const toScad = (v: THREE.Vector3): V3 => [v.x, -v.z, v.y];
const fmt = (n: number) => (Math.abs(n) < 0.005 ? '0' : n.toFixed(2));

/**
 * Interactive measuring: point-to-point distance (with SCAD ΔX/ΔY/ΔZ),
 * angle between two faces, and radius/diameter from three points on an arc.
 * Picks snap to vertices, edge midpoints and face centers (see
 * ViewportManager.pickSnapPoint). Results stay until cleared or the tool
 * is turned off.
 */
export class MeasureTool {
  private mode: MeasureMode | null = null;
  private pending: MeasurePick[] = [];
  private pendingObjects: THREE.Object3D[] = [];
  private measurements: Measurement[] = [];
  private readonly group = new THREE.Group();
  private readonly overlay: HTMLDivElement;
  private readonly hoverMarker: THREE.Points;
  private readonly rubberBand: THREE.Line;
  private statusCallback: ((text: string | null) => void) | null = null;
  /** Show the snap hover marker even when no measure mode is active (one-shot picks). */
  private hoverOnly = false;
  /** Hovered face: outline + corner/center dots so snap targets are visible. */
  private readonly faceOutline: THREE.LineSegments;
  private readonly faceCorners: THREE.Points;
  private readonly faceCenter: THREE.Points;
  private hoverFrame = 0;
  private pendingHover: PointerEvent | null = null;

  constructor(
    private readonly container: HTMLElement,
    scene: THREE.Scene,
    private readonly getCamera: () => THREE.Camera,
    private readonly pick: (clientX: number, clientY: number) => MeasurePick | null,
  ) {
    this.group.renderOrder = 10;
    scene.add(this.group);
    this.overlay = document.createElement('div');
    this.overlay.className = 'measure-overlay';
    container.appendChild(this.overlay);

    this.hoverMarker = this.makePoints([new THREE.Vector3()], 0xffffff, 11);
    this.hoverMarker.visible = false;
    this.group.add(this.hoverMarker);

    this.rubberBand = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), new THREE.Vector3()]),
      new THREE.LineDashedMaterial({ color: LINE_COLOR, dashSize: 1, gapSize: 0.6, depthTest: false, transparent: true, opacity: 0.8 }),
    );
    this.rubberBand.visible = false;
    this.rubberBand.renderOrder = 10;
    this.group.add(this.rubberBand);

    this.faceOutline = new THREE.LineSegments(
      new THREE.BufferGeometry(),
      new THREE.LineBasicMaterial({ color: 0xffb020, depthTest: false, transparent: true, opacity: 0.7 }),
    );
    this.faceOutline.renderOrder = 9;
    this.faceCorners = this.makePoints([], 0x00d084, 6);
    this.faceCenter = this.makePoints([], 0xffb020, 8);
    for (const o of [this.faceOutline, this.faceCorners, this.faceCenter]) { o.visible = false; this.group.add(o); }
  }

  private hideFaceHints(): void {
    this.faceOutline.visible = this.faceCorners.visible = this.faceCenter.visible = false;
  }

  private showFaceHints(face: NonNullable<MeasurePick['face']>): void {
    this.faceOutline.geometry.dispose();
    this.faceOutline.geometry = new THREE.BufferGeometry().setFromPoints(face.edges.flat());
    this.faceCorners.geometry.dispose();
    this.faceCorners.geometry = new THREE.BufferGeometry().setFromPoints(face.corners);
    this.faceCenter.geometry.dispose();
    this.faceCenter.geometry = new THREE.BufferGeometry().setFromPoints(face.center ? [face.center] : []);
    this.faceOutline.visible = face.edges.length > 0;
    this.faceCorners.visible = face.corners.length > 0;
    this.faceCenter.visible = !!face.center;
  }

  getMode(): MeasureMode | null {
    return this.mode;
  }

  isActive(): boolean {
    return this.mode !== null;
  }

  /** Switch mode (null = off, which also clears all measurements). */
  setMode(mode: MeasureMode | null): void {
    this.cancelPending();
    this.mode = mode;
    if (!mode) {
      this.clear();
      this.hoverMarker.visible = false;
      this.hideFaceHints();
    }
    this.emitStatus();
  }

  onStatus(cb: (text: string | null) => void): void {
    this.statusCallback = cb;
  }

  /** Drop the in-progress measurement. Returns true if there was one. */
  cancelPending(): boolean {
    const had = this.pending.length > 0;
    this.pending = [];
    for (const o of this.pendingObjects) this.disposeObject(o);
    this.pendingObjects = [];
    this.rubberBand.visible = false;
    if (had) this.emitStatus();
    return had;
  }

  clear(): void {
    this.cancelPending();
    for (const m of this.measurements) {
      for (const o of m.objects) this.disposeObject(o);
      m.label.remove();
    }
    this.measurements = [];
  }

  getMeasurementTexts(): string[] {
    return this.measurements.map(m => m.label.textContent ?? '');
  }

  setHoverOnly(enabled: boolean): void {
    this.hoverOnly = enabled;
    if (!enabled && !this.mode) { this.hoverMarker.visible = false; this.hideFaceHints(); }
  }

  /** Pointer moved: picks are coalesced to one per animation frame. */
  handleHover(e: PointerEvent): void {
    if (!this.mode && !this.hoverOnly) return;
    this.pendingHover = e;
    if (this.hoverFrame) return;
    this.hoverFrame = requestAnimationFrame(() => {
      this.hoverFrame = 0;
      const ev = this.pendingHover;
      this.pendingHover = null;
      if (ev && (this.mode || this.hoverOnly)) this.hoverNow(ev);
    });
  }

  private hoverNow(e: PointerEvent): void {
    const p = this.pick(e.clientX, e.clientY);
    if (!p) {
      this.hoverMarker.visible = false;
      this.rubberBand.visible = false;
      this.hideFaceHints();
      return;
    }
    if (p.face) this.showFaceHints(p.face); else this.hideFaceHints();
    this.setPointsPosition(this.hoverMarker, p.point);
    (this.hoverMarker.material as THREE.PointsMaterial).color.setHex(SNAP_COLORS[p.kind]);
    this.hoverMarker.visible = true;
    const last = this.pending[this.pending.length - 1];
    if (last && this.mode === 'distance') {
      this.rubberBand.geometry.setFromPoints([last.point, p.point]);
      this.rubberBand.computeLineDistances();
      this.rubberBand.visible = true;
    }
  }

  /** Handle a click while active. Returns true when the click was consumed. */
  handleClick(e: PointerEvent | MouseEvent): boolean {
    if (!this.mode) return false;
    const p = this.pick(e.clientX, e.clientY);
    if (!p) return true;
    this.pending.push(p);
    const marker = this.makePoints([p.point], LINE_COLOR, 8);
    this.group.add(marker);
    this.pendingObjects.push(marker);
    if (this.pending.length >= PICKS_NEEDED[this.mode]) this.complete();
    this.emitStatus();
    return true;
  }

  /** Re-project labels; call once per frame. */
  update(): void {
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    if (!w || !h || this.measurements.length === 0) return;
    const v = new THREE.Vector3();
    const cam = this.getCamera();
    for (const m of this.measurements) {
      v.copy(m.anchor).project(cam);
      if (v.z > 1) { m.label.style.display = 'none'; continue; }
      m.label.style.display = '';
      m.label.style.left = `${(v.x * 0.5 + 0.5) * w}px`;
      m.label.style.top = `${(-v.y * 0.5 + 0.5) * h}px`;
    }
  }

  dispose(): void {
    this.clear();
    this.group.parent?.remove(this.group);
    this.overlay.remove();
  }

  // --- internals ---

  private complete(): void {
    const picks = this.pending;
    const objects = this.pendingObjects;
    this.pending = [];
    this.pendingObjects = [];
    this.rubberBand.visible = false;

    let text: string | null = null;
    let anchor = picks[0].point.clone();
    if (this.mode === 'distance') {
      const [a, b] = picks;
      const d = distance(toScad(a.point), toScad(b.point));
      const [dx, dy, dz] = toScad(b.point.clone().sub(a.point));
      text = `${fmt(d)} mm  (ΔX ${fmt(Math.abs(dx))} ΔY ${fmt(Math.abs(dy))} ΔZ ${fmt(Math.abs(dz))})`;
      objects.push(this.makeLine([a.point, b.point]));
      anchor = a.point.clone().add(b.point).multiplyScalar(0.5);
    } else if (this.mode === 'angle') {
      const [a, b] = picks;
      const deg = angleBetweenNormals(toScad(a.normal), toScad(b.normal));
      text = `${deg.toFixed(2)}°${deg < 0.01 ? ' (parallel faces)' : deg > 179.99 ? ' (opposite faces)' : ''}`;
      objects.push(this.makeLine([a.point, b.point]));
      anchor = a.point.clone().add(b.point).multiplyScalar(0.5);
    } else if (this.mode === 'radius') {
      const c = circleFrom3Points(
        picks[0].point.toArray() as V3, picks[1].point.toArray() as V3, picks[2].point.toArray() as V3,
      );
      if (c) {
        text = `R ${fmt(c.radius)}  ⌀ ${fmt(c.radius * 2)} mm`;
        anchor = new THREE.Vector3(...c.center);
        objects.push(this.makeCircle(anchor, c.radius, new THREE.Vector3(...c.normal)));
        objects.push(this.makePoints([anchor], LINE_COLOR, 6));
      } else {
        text = 'Points are collinear';
      }
    }

    const label = document.createElement('div');
    label.className = 'measure-label';
    label.textContent = text ?? '';
    this.overlay.appendChild(label);
    for (const o of objects) if (!o.parent) this.group.add(o);
    this.measurements.push({ objects, label, anchor });
    this.update();
  }

  private emitStatus(): void {
    if (!this.statusCallback) return;
    if (!this.mode) { this.statusCallback(null); return; }
    const prompts = PROMPTS[this.mode];
    this.statusCallback(`${prompts[Math.min(this.pending.length, prompts.length - 1)]} · Esc to cancel`);
  }

  private makePoints(pts: THREE.Vector3[], color: number, size: number): THREE.Points {
    const pointsObj = new THREE.Points(
      new THREE.BufferGeometry().setFromPoints(pts),
      new THREE.PointsMaterial({ color, size, sizeAttenuation: false, depthTest: false, transparent: true }),
    );
    pointsObj.renderOrder = 11;
    return pointsObj;
  }

  private setPointsPosition(obj: THREE.Points, p: THREE.Vector3): void {
    const attr = obj.geometry.getAttribute('position') as THREE.BufferAttribute;
    attr.setXYZ(0, p.x, p.y, p.z);
    attr.needsUpdate = true;
    obj.geometry.computeBoundingSphere();
  }

  private makeLine(pts: THREE.Vector3[]): THREE.Line {
    const line = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints(pts),
      new THREE.LineBasicMaterial({ color: LINE_COLOR, depthTest: false, transparent: true }),
    );
    line.renderOrder = 10;
    return line;
  }

  private makeCircle(center: THREE.Vector3, radius: number, normal: THREE.Vector3): THREE.Line {
    const u = new THREE.Vector3().crossVectors(normal, Math.abs(normal.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0)).normalize();
    const v = new THREE.Vector3().crossVectors(normal, u).normalize();
    const pts: THREE.Vector3[] = [];
    for (let i = 0; i <= 96; i++) {
      const t = (i / 96) * Math.PI * 2;
      pts.push(center.clone().addScaledVector(u, Math.cos(t) * radius).addScaledVector(v, Math.sin(t) * radius));
    }
    return this.makeLine(pts);
  }

  private disposeObject(o: THREE.Object3D): void {
    o.parent?.remove(o);
    const withGeo = o as THREE.Object3D & { geometry?: THREE.BufferGeometry; material?: THREE.Material };
    withGeo.geometry?.dispose();
    withGeo.material?.dispose();
  }
}
